import type { PropsWithChildren, HTMLAttributes } from "react";
import {
  Surface,
  SurfaceBody,
  type SurfaceElevation,
  type SurfaceVariant,
} from "./Surface";
import { cn } from "../../utils";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  contentClassName?: string;
  innerClassName?: string;
  paddingMode?: "comfortable" | "compact" | "bleed";
  variant?: SurfaceVariant;
  elevation?: SurfaceElevation;
}

export const Card = ({
  className,
  contentClassName,
  innerClassName,
  paddingMode = "comfortable",
  variant = "default",
  elevation = "surface",
  children,
  ...props
}: PropsWithChildren<CardProps>) => {
  const padding =
    paddingMode === "comfortable"
      ? "comfortable"
      : paddingMode === "compact"
        ? "compact"
        : "bleed";

  return (
    <Surface
      as="div"
      variant={variant}
      elevation={elevation}
      padding="bleed"
      className={className}
      {...props}
    >
      <SurfaceBody className={contentClassName} padded={false}>
        <div
          className={cn(
            padding === "comfortable"
              ? "box-border min-h-0 w-full px-surface-comfortable-x py-surface-comfortable-y"
              : padding === "compact"
                ? "box-border min-h-0 w-full px-surface-compact-x py-surface-compact-y"
                : "box-border min-h-0 w-full p-0",
            innerClassName,
          )}
        >
          {children}
        </div>
      </SurfaceBody>
    </Surface>
  );
};

export const CardContent = ({
  className,
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
  <div className={className ?? "w-full"} {...props}>
    {children}
  </div>
);

export const CardHeader = ({
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 px-surface-compact-x pt-surface-compact-x",
      className,
    )}
    {...props}
  />
);

export const CardTitle = ({
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLHeadingElement>>) => (
  <h3
    className={cn(
      "text-heading-md font-semibold tracking-tight text-content-primary",
      className,
    )}
    {...props}
  />
);

export const CardDescription = ({
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLParagraphElement>>) => (
  <p className={cn("text-body text-content-muted", className)} {...props} />
);

export const CardFooter = ({
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
  <div
    className={cn(
      "flex items-center px-surface-compact-x pb-surface-compact-x",
      className,
    )}
    {...props}
  />
);

export default Card;
